/**
 * product-inquiry.js
 *
 * - Opens/closes the available-product inquiry modal
 * - Moves modal to body to avoid stacking/z-index issues
 * - Allows normal Shopify contact form submit so CAPTCHA works
 * - Saves draft + restores modal after CAPTCHA/redirect return
 * - Cleans up Swym wishlist injections inside the modal
 */

(function () {
  const DRAFT_KEY = "productInquiryDraft";
  const PENDING_KEY = "productInquiryPending";
  const RETURN_URL_KEY = "productInquiryReturnUrl";
  const MODAL_ID_KEY = "productInquiryModalId";


  /* Recover successful inquiries redirected to homepage */

  (function recoverHomepageRedirect() {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      if (pending !== "1") return;

      const currentUrl = new URL(window.location.href);

      // Only act when Shopify confirms a successful submission
      // but redirects to the homepage.
      if (
        currentUrl.pathname !== "/" ||
        currentUrl.searchParams.get("contact_posted") !== "true"
      ) {
        return;
      }

      const savedUrl = sessionStorage.getItem(RETURN_URL_KEY);
      if (!savedUrl) return;

      const productUrl = new URL(savedUrl, window.location.origin);

      // Only return to a product on this same storefront.
      if (
        productUrl.origin !== window.location.origin ||
        !/(^|\/)products\/[^/]+/.test(productUrl.pathname)
      ) {
        return;
      }

      // Preserve Shopify's success confirmation.
      productUrl.searchParams.set("contact_posted", "true");
      productUrl.hash = "contact_form";

      window.location.replace(productUrl.toString());

    } catch (error) {
      console.error("Product inquiry redirect recovery:", error);
    }
  })();


  function ensureModalInBody(modal) {
    if (!modal) return;
    if (modal.parentElement === document.body) return;

    const placeholder = document.createComment("product-inquiry-modal-placeholder");
    modal._productInquiryPlaceholder = placeholder;
    modal.parentNode.insertBefore(placeholder, modal);
    document.body.appendChild(modal);
  }

  function restoreModalToPlaceholder(modal) {
    if (!modal) return;
    const placeholder = modal._productInquiryPlaceholder;

    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(modal, placeholder);
      placeholder.parentNode.removeChild(placeholder);
    }

    modal._productInquiryPlaceholder = null;
  }

  function clearDraft() {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      sessionStorage.removeItem(MODAL_ID_KEY);
    } catch (_) {}
  }

  function fixSwymInModal(modal) {
    if (!modal) return;

    modal.querySelectorAll(".swym-atw-button-container").forEach((el) => el.remove());

    const submit = modal.querySelector("[data-product-inquiry-submit]");
    const actions = modal.querySelector(".product-inquiry-modal__actions");

    if (submit && actions && !actions.contains(submit)) {
      actions.appendChild(submit);
    }
  }

  function setModalOpen(modal) {
    if (!modal) return;

    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("product-inquiry-modal-open");
    document.body.classList.add("product-inquiry-modal-open");
  }

  function setModalClosed(modal) {
    if (!modal) return;

    modal.setAttribute("hidden", "hidden");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("product-inquiry-modal-open");
    document.body.classList.remove("product-inquiry-modal-open");
  }

  function ensureCaptchaNote(modal) {
    if (!modal) return;

    let note = modal.querySelector("[data-product-inquiry-captcha-note]");
    if (note) return;

    note = document.createElement("p");
    note.setAttribute("data-product-inquiry-captcha-note", "");
    note.className = "product-inquiry-modal__captcha-note";
    note.textContent =
      "If you get a CAPTCHA, your inquiry will be sent once it’s completed successfully.";

    const submitBtn = modal.querySelector("[data-product-inquiry-submit]");

    if (submitBtn && submitBtn.parentNode) {
      submitBtn.parentNode.insertBefore(note, submitBtn.nextSibling);
    } else {
      modal.appendChild(note);
    }
  }

  function clearInlineErrors(modal) {
    if (!modal) return;
    modal.querySelectorAll("[data-product-inquiry-error]").forEach((el) => el.remove());
  }

  function showInlineMessage(modal, text) {
    if (!modal) return;

    let node = modal.querySelector("[data-product-inquiry-error]");

    if (!node) {
      node = document.createElement("p");
      node.setAttribute("data-product-inquiry-error", "");
      node.className = "product-inquiry-modal__error";

      const submitBtn = modal.querySelector("[data-product-inquiry-submit]");
      if (submitBtn && submitBtn.parentNode) {
        submitBtn.parentNode.insertBefore(node, submitBtn.nextSibling);
      } else {
        modal.appendChild(node);
      }
    }

    node.textContent = text;
  }

  function saveDraft(form) {
    try {
      const fd = new FormData(form);
      const saved = {};

      for (const [k, v] of fd.entries()) {
        if (typeof v === "string") saved[k] = v;
      }

      const modal = form.closest(".product-inquiry-modal");

      if (modal && modal.id) {
        sessionStorage.setItem(MODAL_ID_KEY, modal.id);
      }

      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(saved));
      sessionStorage.setItem(PENDING_KEY, "1");
      sessionStorage.setItem(RETURN_URL_KEY, window.location.href);
    } catch (_) {}
  }

  function restoreDraftIntoModal(modal) {
    if (!modal) return;

    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== "object") return;

      Object.keys(draft).forEach((name) => {
        const el = Array.from(modal.querySelectorAll("[name]")).find(
          (field) => field.name === name
        );

        if (el && "value" in el) el.value = draft[name];
      });
    } catch (_) {}
  }

  function openModal(modal) {
    if (!modal) return;

    ensureModalInBody(modal);
    setModalOpen(modal);
    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    let tries = 0;
    const iv = setInterval(() => {
      fixSwymInModal(modal);
      tries += 1;
      if (tries >= 10) clearInterval(iv);
    }, 100);
  }

  function closeModal(modal) {
    setModalClosed(modal);
    restoreModalToPlaceholder(modal);
  }

  
function restoreAfterCaptcha() {
  try {
    const pending = sessionStorage.getItem(PENDING_KEY);

    // Only process a return from this inquiry form.
    if (pending !== "1") return;

    const modalId = sessionStorage.getItem(MODAL_ID_KEY);

    const modal = modalId
      ? document.getElementById(modalId)
      : document.querySelector(".product-inquiry-modal");

    // Keep the saved submission if the modal is not ready.
    if (!modal) return;

    const params = new URLSearchParams(window.location.search);

    // Check Shopify's URL confirmation.
    const urlConfirmed =
      params.get("contact_posted") === "true";

    // Check Shopify's native Liquid form confirmation.
    const liquidConfirmed = !!modal.querySelector(
      "[data-product-inquiry-confirmed]"
    );

    // Only show success when Shopify confirms submission.
    const successConfirmed = urlConfirmed || liquidConfirmed;

    openModal(modal);

    if (successConfirmed) {
      clearDraft();

      const form = modal.querySelector("[data-product-inquiry]");

      if (form) {
        form.hidden = true;
        form.style.display = "none";
      }

      let successNode = modal.querySelector(
        "[data-product-inquiry-success]"
      );

      if (!successNode) {
        successNode = document.createElement("div");
        successNode.setAttribute(
          "data-product-inquiry-success",
          ""
        );
        successNode.className =
          "product-inquiry-modal__success";

        const panel = modal.querySelector(
          ".product-inquiry-modal__panel"
        );

        if (panel) {
          panel.appendChild(successNode);
        }
      }

      successNode.textContent =
        "Inquiry sent successfully. We’ll get back to you shortly.";

      // Remove URL success flag without reloading.
      if (urlConfirmed) {
        const url = new URL(window.location.href);
        url.searchParams.delete("contact_posted");

        window.history.replaceState(
          {},
          "",
          url.toString()
        );
      }

    } else {
      restoreDraftIntoModal(modal);

      showInlineMessage(
        modal,
        "Your inquiry has not been confirmed as sent. Please review your information and submit again if necessary."
      );

      // Allow another attempt.
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(MODAL_ID_KEY);
    }

    const submitBtn = modal.querySelector(
      "[data-product-inquiry-submit]"
    );

    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.removeAttribute("aria-busy");
      submitBtn.textContent = "Submit Inquiry";
    }

  } catch (error) {
    console.error(
      "Product inquiry restoration error:",
      error
    );
  }
}

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    restoreAfterCaptcha
  );
} else {
  restoreAfterCaptcha();
}

  document.addEventListener("click", function (e) {
    const openBtn = e.target.closest("[data-product-inquiry-open]");

    if (openBtn) {
      e.preventDefault();

      const id = openBtn.getAttribute("aria-controls");
      const modal = id ? document.getElementById(id) : null;

      openModal(modal);
      return;
    }

    const closeBtn = e.target.closest("[data-product-inquiry-close]");

    if (closeBtn) {
      e.preventDefault();

      const modal = closeBtn.closest(".product-inquiry-modal");
      closeModal(modal);
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;

    document
      .querySelectorAll(".product-inquiry-modal:not([hidden])")
      .forEach(closeModal);
  });

  document.addEventListener("shopify:section:unload", function () {
    document
      .querySelectorAll(".product-inquiry-modal:not([hidden])")
      .forEach(closeModal);
  });

  document.addEventListener("submit", function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const modal = form.closest(".product-inquiry-modal");
    if (!modal) return;

    const submitBtn = form.querySelector("[data-product-inquiry-submit]");
    if (!submitBtn) return;

    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }

    saveDraft(form);

    submitBtn.disabled = true;
    submitBtn.setAttribute("aria-busy", "true");
    submitBtn.textContent = "Submitting…";
  });
})();